import { useEffect, useMemo, useState } from "react";
import { BarChart3, Trophy, Clock, Repeat2 } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";
import { OptimalTimes } from "../../components/OptimalTimes";

type Platform = "twitter" | "bluesky" | "linkedin" | "newsletter" | "short_video";

type TabId = "overview" | "top" | "times" | "autoplug";

const PLATFORM_LABEL: Record<string, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  newsletter: "Newsletter",
  short_video: "Short video",
};

interface SummaryRow {
  platform: string;
  total_published: number;
  total_likes: number;
  total_replies: number;
  total_reposts: number;
  total_impressions: number | null;
}

interface TopPost {
  scheduled_post_id: string;
  platform: string;
  content: string;
  external_post_url: string | null;
  source: string;
  published_at: number;
  likes: number;
  replies: number;
  reposts: number;
  quotes: number;
  impressions: number | null;
  bookmarks: number | null;
  fetched_at: number;
}

interface AutoPlugPair {
  run_id: string;
  config_name: string;
  origin_id: string | null;
  origin_content: string | null;
  origin_url: string | null;
  origin_likes: number | null;
  origin_replies: number | null;
  origin_reposts: number | null;
  reply_id: string | null;
  reply_content: string | null;
  reply_url: string | null;
  reply_likes: number | null;
  reply_replies: number | null;
  reply_reposts: number | null;
  platform: string | null;
  created_at: number;
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10000 ? 1 : 0) + "k";
  return (n / 1_000_000).toFixed(1) + "M";
}

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default function AnalyticsPage() {
  const toast = useToast();
  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState<Platform | "">("");
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [topPosts, setTopPosts] = useState<TopPost[]>([]);
  const [pairs, setPairs] = useState<AutoPlugPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<TabId>("overview");

  async function load() {
    setLoading(true);
    try {
      const platformParam = platform ? `&platform=${platform}` : "";
      const [s, t, p] = await Promise.all([
        api.get<{ by_platform: SummaryRow[] }>(`/api/analytics/summary?days=${days}`),
        api.get<{ posts: TopPost[] }>(
          `/api/analytics/top-posts?days=${days}&limit=20${platformParam}`
        ),
        api.get<{ pairs: AutoPlugPair[] }>("/api/analytics/auto-plug-pairs?limit=20"),
      ]);
      setSummary(s.by_platform ?? []);
      setTopPosts(t.posts ?? []);
      setPairs(p.pairs ?? []);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, platform]);

  async function refreshNow() {
    setRefreshing(true);
    try {
      const res = await api.post<{ refreshed: number; failed: number }>(
        "/api/analytics/refresh-now",
        {}
      );
      if (res.refreshed === 0 && res.failed === 0) {
        toast.success("No posts to refresh yet.");
      } else if (res.failed > 0) {
        toast.success(
          `Refreshed ${res.refreshed} post(s). ${res.failed} failed (check error column).`
        );
      } else {
        toast.success(`Refreshed ${res.refreshed} post(s).`);
      }
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  }

  const filteredSummary = useMemo(
    () => (platform ? summary.filter((r) => r.platform === platform) : summary),
    [summary, platform]
  );

  const totals = useMemo(() => {
    return filteredSummary.reduce(
      (acc, r) => ({
        published: acc.published + r.total_published,
        likes: acc.likes + r.total_likes,
        replies: acc.replies + r.total_replies,
        reposts: acc.reposts + r.total_reposts,
        impressions:
          r.total_impressions != null
            ? (acc.impressions ?? 0) + r.total_impressions
            : acc.impressions,
      }),
      {
        published: 0,
        likes: 0,
        replies: 0,
        reposts: 0,
        impressions: null as number | null,
      }
    );
  }, [filteredSummary]);

  const filteredPairs = useMemo(
    () => (platform ? pairs.filter((p) => p.platform === platform) : pairs),
    [pairs, platform]
  );

  return (
    <div>
      <header className="mb-6">
        <h1 className="page-title">Analytics</h1>
        <p className="page-subtitle">
          Engagement pulled from the platforms every 6 hours. Bluesky is fully
          supported; X requires public_metrics access (free tier); LinkedIn shows
          likes + comments only.
        </p>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div>
          <label className="mr-2 text-sm text-ink-600">Window:</label>
          <select
            className="input inline-block w-auto"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
        <div>
          <label className="mr-2 text-sm text-ink-600">Platform:</label>
          <select
            className="input inline-block w-auto"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform | "")}
          >
            <option value="">All</option>
            <option value="twitter">X / Twitter</option>
            <option value="bluesky">Bluesky</option>
            <option value="linkedin">LinkedIn</option>
          </select>
        </div>
        <button
          onClick={refreshNow}
          disabled={refreshing}
          className="btn-secondary text-sm"
          title="Pull latest metrics from each platform now (rate-limited to once per minute)"
        >
          {refreshing ? "Refreshing…" : "↻ Refresh now"}
        </button>
      </div>

      <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Published" value={fmtNum(totals.published)} />
        <StatCard label="Likes" value={fmtNum(totals.likes)} />
        <StatCard label="Replies" value={fmtNum(totals.replies)} />
        <StatCard label="Reposts" value={fmtNum(totals.reposts)} />
        <StatCard label="Impressions" value={fmtNum(totals.impressions)} />
      </section>

      <div className="mb-5 flex flex-wrap gap-1.5 border-b border-ink-200">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} Icon={BarChart3} label="By platform" />
        <TabButton active={tab === "top"} onClick={() => setTab("top")} Icon={Trophy} label="Top posts" count={topPosts.length} />
        <TabButton active={tab === "times"} onClick={() => setTab("times")} Icon={Clock} label="Best times" />
        <TabButton active={tab === "autoplug"} onClick={() => setTab("autoplug")} Icon={Repeat2} label="Auto-plug ROI" count={filteredPairs.length} />
      </div>

      {tab === "overview" ? (
      <section className="mb-8">
        <h2 className="sr-only">By platform</h2>
        <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left text-ink-600">
              <tr>
                <th className="px-3 py-2 font-medium">Platform</th>
                <th className="px-3 py-2 text-right font-medium">Posts</th>
                <th className="px-3 py-2 text-right font-medium">Likes</th>
                <th className="px-3 py-2 text-right font-medium">Replies</th>
                <th className="px-3 py-2 text-right font-medium">Reposts</th>
                <th className="px-3 py-2 text-right font-medium">Impressions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSummary.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-ink-400">
                    No published posts in this window yet. Schedule one and check back after
                    the next analytics tick.
                  </td>
                </tr>
              ) : (
                filteredSummary.map((row) => (
                  <tr key={row.platform} className="border-t border-ink-100">
                    <td className="px-3 py-2 font-medium text-ink-900">
                      {PLATFORM_LABEL[row.platform] ?? row.platform}
                    </td>
                    <td className="px-3 py-2 text-right">{fmtNum(row.total_published)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(row.total_likes)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(row.total_replies)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(row.total_reposts)}</td>
                    <td className="px-3 py-2 text-right">
                      {fmtNum(row.total_impressions)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}

      {tab === "times" ? (
        <OptimalTimes
          defaultPlatform={
            platform === "twitter" || platform === "linkedin" || platform === "bluesky"
              ? platform
              : "bluesky"
          }
        />
      ) : null}

      {tab === "top" ? (
      <section className="mb-8">
        <h2 className="sr-only">Top posts</h2>
        {topPosts.length === 0 && !loading ? (
          <div className="rounded-lg border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
            No metrics yet. Wait up to 6 hours after a post publishes.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {topPosts.map((p) => (
              <div
                key={p.scheduled_post_id}
                className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                  <span className="font-medium text-ink-700">
                    {PLATFORM_LABEL[p.platform] ?? p.platform}
                  </span>
                  <span>·</span>
                  <span>{fmtDate(p.published_at)}</span>
                  {p.source === "auto_plug" ? (
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-purple-800">
                      auto-plug
                    </span>
                  ) : null}
                </div>
                <p className="mb-3 whitespace-pre-wrap break-words text-sm text-ink-800">
                  {truncate(p.content, 240)}
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-ink-700">
                  <Metric label="Likes" value={p.likes} />
                  <Metric label="Replies" value={p.replies} />
                  <Metric label="Reposts" value={p.reposts} />
                  {p.quotes ? <Metric label="Quotes" value={p.quotes} /> : null}
                  {p.impressions != null ? (
                    <Metric label="Impressions" value={p.impressions} />
                  ) : null}
                  {p.bookmarks != null ? (
                    <Metric label="Bookmarks" value={p.bookmarks} />
                  ) : null}
                </div>
                {p.external_post_url ? (
                  <a
                    href={p.external_post_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs text-brand-600 hover:underline"
                  >
                    View post →
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
      ) : null}

      {tab === "autoplug" ? (
      <section className="mb-8">
        <h2 className="sr-only">Auto-plug ROI</h2>
        <p className="mb-3 text-xs text-ink-500">
          Compare each original post against its auto-plug follow-up.
        </p>
        {filteredPairs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
            No auto-plug pairs yet. Set up an auto-plug and post on a covered platform.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPairs.map((pr) => (
              <div
                key={pr.run_id}
                className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-2 text-xs text-ink-500">
                  <span className="font-medium text-ink-700">{pr.config_name}</span>
                  {pr.platform ? (
                    <>
                      <span>·</span>
                      <span>{PLATFORM_LABEL[pr.platform] ?? pr.platform}</span>
                    </>
                  ) : null}
                  <span>·</span>
                  <span>{fmtDate(pr.created_at)}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <PairCard
                    title="Original"
                    color="ink"
                    content={pr.origin_content}
                    url={pr.origin_url}
                    likes={pr.origin_likes}
                    replies={pr.origin_replies}
                    reposts={pr.origin_reposts}
                  />
                  <PairCard
                    title={
                      pr.platform === "bluesky" || pr.platform === "twitter"
                        ? "↳ Auto-plug reply"
                        : "Auto-plug follow-up"
                    }
                    color="brand"
                    content={pr.reply_content}
                    url={pr.reply_url}
                    likes={pr.reply_likes}
                    replies={pr.reply_replies}
                    reposts={pr.reply_reposts}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      ) : null}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-brand-500 text-brand-700"
          : "border-transparent text-ink-500 hover:text-ink-900"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
      {typeof count === "number" && count > 0 ? (
        <span
          className={`ml-1 rounded-full px-1.5 py-0.5 text-2xs ${
            active ? "bg-brand-100 text-brand-700" : "bg-ink-100 text-ink-600"
          }`}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-ink-900">{value}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span className="font-semibold text-ink-900">{fmtNum(value)}</span>{" "}
      <span className="text-ink-500">{label}</span>
    </div>
  );
}

function PairCard(props: {
  title: string;
  color: "ink" | "brand";
  content: string | null;
  url: string | null;
  likes: number | null;
  replies: number | null;
  reposts: number | null;
}) {
  const bg = props.color === "brand" ? "bg-brand-50/40 border-brand-100" : "bg-ink-50";
  return (
    <div className={`rounded-lg border p-3 text-sm ${bg}`}>
      <div className="mb-1 text-xs uppercase tracking-wide text-ink-500">
        {props.title}
      </div>
      <p className="mb-2 whitespace-pre-wrap break-words text-ink-800">
        {props.content ? truncate(props.content, 200) : "—"}
      </p>
      <div className="flex gap-3 text-xs text-ink-700">
        <Metric label="L" value={props.likes ?? 0} />
        <Metric label="R" value={props.replies ?? 0} />
        <Metric label="RT" value={props.reposts ?? 0} />
      </div>
      {props.url ? (
        <a
          href={props.url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-block text-xs text-brand-600 hover:underline"
        >
          View →
        </a>
      ) : null}
    </div>
  );
}
